/**
 * @fileoverview Integrations Orbit - animated integration showcase
 *
 * Purpose: Visualise how Morgan connects to the third party tools a firm runs
 * on. The Morgan mark sits at the centre while the integration logos orbit
 * around it, joined by animated "data flow" lines and travelling pulse dots.
 *
 * Layout / alignment strategy:
 * - A square stage is centred in the available space. Two layers sit on top of
 *   each other and are positioned with the SAME percentage coordinates, so they
 *   are guaranteed to align regardless of the stage's exact pixel size:
 *     1. An SVG layer (connection lines + travelling pulse dots). The SVG has NO
 *        `viewBox` — its line/dot coordinates are expressed as percentages,
 *        which resolve against the SVG's own width (x) and height (y), exactly
 *        like CSS `left`/`top` percentages.
 *     2. An HTML overlay (the central hub + six logo chips) positioned with the
 *        matching percentages.
 *   Because both layers use plain percentages against the same box, there is no
 *   `viewBox`/`preserveAspectRatio` scaling that could make them diverge.
 *
 * Why HTML overlays instead of `<foreignObject>`:
 * - Safari/WebKit has long-standing bugs rendering HTML inside SVG
 *   `<foreignObject>` (CSS-transform animations + positioning), which made the
 *   chips and hub render incorrectly. Plain absolutely-positioned HTML on top of
 *   the SVG renders identically across Chrome, Safari and Firefox.
 *
 * Motion:
 * - Logo chips gently float (staggered via animationDelay).
 * - Connection lines flow via an animated dash overlay.
 * - Pulse dots travel from each logo toward the centre. We animate the dots'
 *   `cx`/`cy` attributes (NOT `<animateMotion>`): Safari ignores coordinate
 *   scaling for `animateMotion` paths, which made the dots drift off the lines.
 * - The central hub emits a slow pulsing ring.
 * - All CSS-driven motion is disabled under prefers-reduced-motion.
 *
 * Used by: IntegrationsSection (first feature card).
 */

import { Logo } from "@/ui/layout/logo";

/**
 * A single integration node positioned around the central hub.
 *
 * - `cx`/`cy` are coordinates within the 280-unit content stage (before the
 *   margin is applied). They drive the connection line, the travelling pulse dot
 *   AND the chip's overlay position.
 * - `logo` is an image source, or `wordmark` renders a styled text chip when no
 *   logo asset is available (MYOB).
 */
type IntegrationNode = {
  name: string;
  cx: number;
  cy: number;
  delay: string;
} & ({ logo: string; wordmark?: never } | { wordmark: string; logo?: never });

/** Content-stage centre (the 280-unit stage, before the margin offset). */
const CENTER = 140;

/**
 * Margin (in stage units) added around the 280-unit content stage so floating
 * chips and their shadows have room before the overflow-hidden container clips.
 * This pulls the whole orbit inward from the square's edges; it is large enough
 * that the top/bottom chips (Outlook / MYOB) plus their float travel never reach
 * the edge. The combined coordinate space is (280 + 2 × MARGIN) per side.
 */
const MARGIN = 34;

/** Full coordinate space side length (content stage + margin on each side). */
const STAGE = CENTER * 2 + MARGIN * 2;

/** Percentage string for the stage centre (line origin, dot destination). */
const CENTER_PCT = `${((CENTER + MARGIN) / STAGE) * 100}%`;

/**
 * Map a content-stage coordinate (0–280) to a percentage string of the full
 * square, accounting for the margin offset. The SAME value is used for the SVG
 * line/dot coordinates and the HTML chip position, guaranteeing alignment.
 */
function toPercent(coord: number): string {
  return `${((coord + MARGIN) / STAGE) * 100}%`;
}

/** Six integration nodes spaced 60° apart on a radius of 110, starting at top. */
const NODES: ReadonlyArray<IntegrationNode> = [
  {
    name: "Outlook",
    logo: "/logos/outlook.svg",
    cx: CENTER,
    cy: 30,
    delay: "0s",
  },
  { name: "Xero", logo: "/logos/xero.svg", cx: 235.3, cy: 85, delay: "0.6s" },
  {
    name: "OneDrive",
    logo: "/logos/onedrive.svg",
    cx: 235.3,
    cy: 195,
    delay: "1.2s",
  },
  { name: "MYOB", wordmark: "MYOB", cx: CENTER, cy: 250, delay: "1.8s" },
  {
    name: "InfoTrack",
    logo: "/logos/infotrack.png",
    cx: 44.7,
    cy: 195,
    delay: "2.4s",
  },
  {
    name: "SharePoint",
    logo: "/logos/sharepoint.svg",
    cx: 44.7,
    cy: 85,
    delay: "3s",
  },
];

/**
 * Animated radial showcase of Morgan's third party integrations.
 *
 * @component
 * @example
 * <IntegrationsOrbit />
 */
export function IntegrationsOrbit() {
  return (
    <div
      className="flex h-full w-full items-center justify-center"
      aria-hidden="true"
    >
      {/* Square stage centred in the available space (the placeholder is always
          wider than tall, so height is the limiting dimension). Both layers fill
          this box and use the same percentage coordinates, so they stay aligned
          even if the box is ever not perfectly square. */}
      <div className="relative aspect-square h-full">
        {/* Layer 1: connection lines + travelling pulse dots. No viewBox — the
            percentage coordinates resolve against this SVG's own width/height,
            matching the CSS percentages used by the chips below. */}
        <svg
          className="absolute inset-0 h-full w-full text-primary"
          fill="none"
        >
          {NODES.map((node) => {
            const x = toPercent(node.cx);
            const y = toPercent(node.cy);
            return (
              <g key={`line-${node.name}`}>
                {/* Faint base line */}
                <line
                  x1={CENTER_PCT}
                  y1={CENTER_PCT}
                  x2={x}
                  y2={y}
                  stroke="currentColor"
                  strokeOpacity={0.12}
                  strokeWidth={1.5}
                />
                {/* Flowing dashed overlay */}
                <line
                  x1={CENTER_PCT}
                  y1={CENTER_PCT}
                  x2={x}
                  y2={y}
                  stroke="currentColor"
                  strokeOpacity={0.45}
                  strokeWidth={1.5}
                  strokeDasharray="2 6"
                  className="animate-dash-flow motion-reduce:animate-none"
                  style={{ animationDelay: node.delay }}
                />
                {/* Travelling pulse dot (logo → centre), cx/cy animated. */}
                <circle
                  cx={x}
                  cy={y}
                  r={3}
                  fill="currentColor"
                  fillOpacity={0.85}
                >
                  <animate
                    attributeName="cx"
                    values={`${x};${CENTER_PCT}`}
                    keyTimes="0;1"
                    calcMode="linear"
                    dur="2.4s"
                    begin={node.delay}
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="cy"
                    values={`${y};${CENTER_PCT}`}
                    keyTimes="0;1"
                    calcMode="linear"
                    dur="2.4s"
                    begin={node.delay}
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="fill-opacity"
                    values="0;0.85;0.85;0"
                    keyTimes="0;0.1;0.85;1"
                    dur="2.4s"
                    begin={node.delay}
                    repeatCount="indefinite"
                  />
                </circle>
              </g>
            );
          })}
        </svg>

        {/* Layer 2: central Morgan hub (HTML overlay, centred). */}
        <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center">
          <span className="absolute inset-0 -z-10 rounded-full bg-primary/20 motion-safe:animate-hub-pulse" />
          <span className="flex h-16 w-16 items-center justify-center rounded-full border border-primary/15 bg-background shadow-md shadow-primary/10">
            <Logo compact width={30} height={30} className="text-primary" />
          </span>
        </div>

        {/* Layer 2: integration logo chips (HTML overlay, centred on each node). */}
        {NODES.map((node) => (
          <div
            key={`chip-${node.name}`}
            className="absolute -translate-x-1/2 -translate-y-1/2"
            style={{ left: toPercent(node.cx), top: toPercent(node.cy) }}
          >
            <div
              className="motion-safe:animate-logo-float"
              style={{ animationDelay: node.delay }}
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full border border-primary/10 bg-white shadow-sm shadow-primary/10">
                {node.logo ? (
                  <img
                    src={node.logo}
                    alt={node.name}
                    className="h-7 w-7 object-contain"
                  />
                ) : (
                  <span className="text-[10px] font-bold tracking-tight text-[#6100A5]">
                    {node.wordmark}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
